import React, { useCallback } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import styles from './styles.module.less';
import { FileExtension } from '../../../enums/file-extension';
import { MediaType } from '../../../enums/media-type';
import { ProductDocumentType } from '../../../enums/product-document-type';
import { useSellerFormattedDate } from '../../../hooks/use-datetime-formats';
import { getCurrentDateTimeAsUtcIso } from '../../../services/datetime-service';
import { downloadFileFromBlob } from '../../../services/document-service';
import { uploadProductDocument, downloadProductDocument, deleteProductDocument } from '../../../store/products/products.api';
import { FileUploadButton } from '../../file-upload-button/file-upload-button';

const MAX_FILE_SIZE_MB = 5;
const ACCEPTED_FILE_TYPES = [FileExtension.PDF];

/**
 * @typedef {object} MsdsFile
 * @property {string} fileName
 * @property {string} uploadDate
 */

/**
 * @param {object} props
 * @param {string} props.productId
 * @param {MsdsFile | null} props.currentMsdsFile
 * @param {boolean} [props.isDisabled]
 * @param {(file: MsdsFile | null) => void} props.setCurrentMsdsFile
 * @returns {React.JSX.Element}
 */
export const MsdsUpload = ({ productId, currentMsdsFile, isDisabled = false, setCurrentMsdsFile }) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [errorMessage, setErrorMessage] = useAsyncSafeState(null);

  const uploadDate = useSellerFormattedDate(currentMsdsFile?.uploadDate);

  const handleFileSelect = useCallback(
    async (file) => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        await uploadProductDocument(productId, ProductDocumentType.MaterialSafetyDataSheet, file);
        setCurrentMsdsFile({
          fileName: file.name,
          uploadDate: getCurrentDateTimeAsUtcIso(),
        });
      } catch {
        setErrorMessage(intl.formatMessage(messages.uploadError));
      } finally {
        setIsLoading(false);
      }
    },
    [productId, setCurrentMsdsFile, setIsLoading, setErrorMessage, intl],
  );

  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await downloadProductDocument(productId, ProductDocumentType.MaterialSafetyDataSheet);
      const blob = new Blob([response.data], { type: MediaType.ApplicationPDF });
      downloadFileFromBlob(blob, currentMsdsFile.fileName);
    } catch {
      setErrorMessage(intl.formatMessage(messages.downloadError));
    } finally {
      setIsLoading(false);
    }
  }, [productId, currentMsdsFile, setIsLoading, setErrorMessage, intl]);

  const handleRemove = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await deleteProductDocument(productId, ProductDocumentType.MaterialSafetyDataSheet);
      setCurrentMsdsFile(null);
    } catch {
      setErrorMessage(intl.formatMessage(messages.removeError));
    } finally {
      setIsLoading(false);
    }
  }, [productId, setCurrentMsdsFile, setIsLoading, setErrorMessage, intl]);

  return (
    <div className={styles['msds-upload']}>
      <Text textType={TextType.Label} value={messages.uploadMsdsTitle} />
      <div className={styles['upload-section']}>
        <FileUploadButton
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          isDisabled={isDisabled || isLoading || Boolean(currentMsdsFile)}
          maxFileSizeMB={MAX_FILE_SIZE_MB}
          onFileSelect={handleFileSelect}
          onUploadError={setErrorMessage}
        />
        {currentMsdsFile ? (
          <div className={styles['file-info']}>
            <Button
              className={styles['file-name-button']}
              disabled={isDisabled || isLoading}
              onClick={handleDownload}
              theme={THEME_CONSTANTS.link}
            >
              {currentMsdsFile.fileName}
            </Button>
            {uploadDate ? <Text className={styles['upload-date']} textType={TextType.Caption} value={uploadDate} /> : null}
            <Button
              className={styles['remove-button']}
              disabled={isDisabled || isLoading}
              onClick={handleRemove}
              theme={THEME_CONSTANTS.unstyled}
            >
              <FontAwesomeIcon className={styles['remove-button-icon']} icon={faXmark} />
              <FormattedMessage {...messages.removeButton} />
            </Button>
          </div>
        ) : null}
        {isLoading ? <LoadingIndicator className={styles['loading-indicator']} /> : null}
      </div>
      {errorMessage ? <Text className={styles['error-message']} textType={TextType.Caption} value={errorMessage} /> : null}
    </div>
  );
};
